import React, { useState, useRef, useEffect } from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import { chatbotData } from './chatbotData';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/api';

// Sub-components
import ChatHeader from './ChatHeader';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

const Chatbot = () => {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [language, setLanguage] = useState('en');
    const [currentFlow, setCurrentFlow] = useState(null);
    const [flowStep, setFlowStep] = useState(null);
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "Hi! 👋 Welcome to NammaEventz Worker Support. Please select your language.",
            sender: 'bot',
            timestamp: new Date(),
            isSystem: true
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [quickActions, setQuickActions] = useState(["English", "தமிழ்"]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const messagesEndRef = useRef(null);

    const toggleChat = () => setIsOpen(!isOpen);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    useEffect(() => {
        if (isOpen) {
            fetchTicketUpdates();
            const interval = setInterval(fetchTicketUpdates, 30000);
            return () => clearInterval(interval);
        }
    }, [isOpen]);

    const addBotMessage = (text, options = []) => {
        setTimeout(() => {
            setMessages(prev => [...prev, {
                id: Date.now(),
                text: text,
                sender: 'bot',
                timestamp: new Date()
            }]);
            setQuickActions(options);
        }, 500);
    };

    const addUserMessage = (text, image = null) => {
        setMessages(prev => [...prev, {
            id: Date.now(),
            text: text,
            image: image,
            sender: 'user',
            timestamp: new Date()
        }]);
    };

    const handleImageSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'ml_default'); // Replace with your Cloudinary preset

        try {
            const res = await axios.post(
                'https://api.cloudinary.com/v1_1/dchd2x4m7/image/upload', // Replace with your Cloudinary URL
                formData
            );
            return res.data.secure_url;
        } catch (err) {
            console.error("Upload failed", err);
            return null;
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() && !selectedImage) return;

        let imageUrl = null;
        if (selectedImage) {
            setIsUploading(true);
            imageUrl = await uploadImage(selectedImage);
            setIsUploading(false);
            setSelectedImage(null);
        }

        const text = inputValue;
        setInputValue('');
        addUserMessage(text, imageUrl);

        // If in ticket creation mode (escalation)
        if (flowStep === 'ticket_description') {
            createTicket(text, imageUrl);
        } else {
            processMessage(text);
        }
    };

    // Fetch active tickets and sync messages
    const fetchTicketUpdates = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/tickets`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const tickets = res.data.tickets;
            if (tickets && tickets.length > 0) {
                const activeTicket = tickets.find(t => t.status === 'open' || t.status === 'in_progress');

                if (activeTicket) {
                    const lastAdminMsg = activeTicket.messages.filter(m => m.sender === 'admin').pop();

                    if (lastAdminMsg) {
                        setMessages(prevMessages => {
                            // Check if this specific message text already exists from 'bot' to avoid duplicates
                            const alreadyExists = prevMessages.some(m =>
                                m.sender === 'bot' && m.text === `Admin: ${lastAdminMsg.text}`
                            );

                            if (!alreadyExists) {
                                return [...prevMessages, {
                                    id: Date.now(), // Unique ID for new message
                                    text: `Admin: ${lastAdminMsg.text}`,
                                    sender: 'bot',
                                    timestamp: new Date()
                                }];
                            }
                            return prevMessages;
                        });
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching tickets:", err);
        }
    };

    const createTicket = async (description, imageUrl) => {
        try {
            addBotMessage(language === 'en' ? "Creating ticket..." : "Ticket create panren...");

            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const res = await axios.post(`${API_URL}/api/tickets`, {
                category: currentFlow || 'other',
                description: description,
                priority: 'medium',
                images: imageUrl ? [imageUrl] : []
            }, config);

            const ticketId = res.data.ticket.ticketId;
            addBotMessage(
                (language === 'en'
                    ? `✅ Ticket Created! ID: ${ticketId}. Our admin will review it.`
                    : `✅ Ticket Create Aayiduchu! ID: ${ticketId}. Admin idhai parpar.`),
                ["Main Menu"]
            );
            setCurrentFlow(null);
            setFlowStep(null);
        } catch (err) {
            console.error("Error creating ticket:", err.response?.data || err.message);
            const errorMessage = err.response?.data?.message || "Error creating ticket. Please try again later.";
            addBotMessage(`❌ ${errorMessage}`, ["Main Menu"]);
        }
    };

    const handleQuickAction = (action) => {
        addUserMessage(action);
        processMessage(action);
    };

    const handleReset = () => {
        setCurrentFlow(null);
        setFlowStep(null);
        setQuickActions(language === 'en'
            ? chatbotData.scripts.start.en.options
            : chatbotData.scripts.start.ta.options
        );
        addBotMessage(chatbotData.scripts.start[language].text, chatbotData.scripts.start[language].options);
    };

    const toggleLanguage = () => {
        const newLang = language === 'en' ? 'ta' : 'en';
        setLanguage(newLang);
        setMessages(prev => [...prev, {
            id: Date.now(),
            text: newLang === 'en'
                ? chatbotData.scripts.start.en.text
                : chatbotData.scripts.start.ta.text,
            sender: 'bot',
            timestamp: new Date()
        }]);
        setQuickActions(newLang === 'en'
            ? chatbotData.scripts.start.en.options
            : chatbotData.scripts.start.ta.options);
    };

    const processMessage = (text) => {
        const lowerText = text.toLowerCase();

        // 1. Language Selection
        if (text === "English") {
            setLanguage('en');
            addBotMessage(chatbotData.scripts.start.en.text, chatbotData.scripts.start.en.options);
            return;
        } else if (text === "தமிழ்") {
            setLanguage('ta');
            addBotMessage(chatbotData.scripts.start.ta.text, chatbotData.scripts.start.ta.options);
            return;
        }

        // 2. Main Menu / Reset
        if (text === "Main Menu" || text === "Start Over") {
            handleReset();
            return;
        }

        // 3. Escalation Trigger
        if (text.includes("Still not working") || text.includes("Innum work aagala") || text.includes("Ticket")) {
            startEscalation();
            return;
        }

        // 4. Flow Handling
        if (currentFlow) {
            handleFlowLogic(text);
            return;
        }

        // 5. Intent Matching
        let matchedIntent = null;
        if (text.includes("Account") || text.includes("Login")) matchedIntent = "account_login_help";
        else if (text.includes("Profile")) matchedIntent = "profile_update";
        else if (text.includes("Payment") || text.includes("Subscription")) matchedIntent = "subscription_payment";
        else if (text.includes("Booking")) matchedIntent = "booking_issue";
        else if (text.includes("Technical")) matchedIntent = "technical_issue";
        else if (text.includes("Pricing")) matchedIntent = "pricing_help";
        else if (text.includes("Safety") || text.includes("Complaint")) matchedIntent = "safety_complaint";
        else if (text.includes("Support")) matchedIntent = "talk_to_support";

        if (!matchedIntent) {
            for (const intent of chatbotData.intents) {
                const keywords = intent.keywords[language] || intent.keywords.en;
                if (keywords.some(k => lowerText.includes(k))) {
                    matchedIntent = intent.id;
                    break;
                }
            }
        }

        if (matchedIntent) {
            startFlow(matchedIntent);
        } else {
            addBotMessage(chatbotData.scripts.fallback[language].text, chatbotData.scripts.fallback[language].options);
        }
    };

    const startFlow = (intentId) => {
        setCurrentFlow(intentId);
        setFlowStep('start');
        const script = chatbotData.scripts[intentId]?.start?.[language];
        if (script) {
            addBotMessage(script.text, script.options || []);
        }
    };

    const startEscalation = () => {
        setFlowStep('ticket_description');
        addBotMessage(
            language === 'en'
                ? "I see the issue persists. Please describe the problem in detail and upload a screenshot if possible. This will be sent to the Admin Panel."
                : "Prachana innum sariyaagala polarku. Dayavu seidhu vivarama sollunga, screenshot irundha anuppunga. Idhu Admin-ku pogum.",
            []
        );
    };

    const handleFlowLogic = (text) => {
        // Specific Flow Logic (Simplified for brevity, same logic as before)
        switch (currentFlow) {
            case 'account_login_help':
                if (flowStep === 'start') {
                    if (text.includes("OTP")) {
                        setFlowStep('otp_issue');
                        const s = chatbotData.scripts.account_login_help.otp_issue[language];
                        addBotMessage(s.text, s.options);
                    } else {
                        startEscalation();
                    }
                } else if (flowStep === 'otp_issue') {
                    if (text.includes("Still") || text.includes("Innum")) {
                        startEscalation();
                    } else {
                        addBotMessage("Glad I could help!", ["Main Menu"]);
                        setCurrentFlow(null);
                    }
                }
                break;
            default:
                startEscalation();
        }
    };

    // Access Control: Only show for workers
    if (!user || user.role !== 'worker') {
        return null;
    }

    return (
        <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-40 font-sans">
            {/* Chat Window */}
            <div className={`
                absolute bottom-0 right-0 mb-20 md:mb-24
                transition-all duration-300 origin-bottom-right
                ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}
            `}>
                <div className="bg-white w-[85vw] md:w-[400px] h-[80vh] md:h-[600px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100">

                    <ChatHeader
                        language={language}
                        toggleLanguage={toggleLanguage}
                        onClose={toggleChat}
                        onReset={handleReset}
                    />

                    <div className="flex-1 overflow-y-auto p-4 bg-gray-50 scrollbar-thin scrollbar-thumb-gray-200">
                        {messages.map((msg) => (
                            <ChatMessage key={msg.id} message={msg} />
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="bg-gray-50 px-4 pb-2">
                        {quickActions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {quickActions.map((action, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleQuickAction(action)}
                                        className="bg-white text-cherry-700 text-xs font-semibold px-4 py-2 rounded-full border border-cherry-100 shadow-sm hover:bg-cherry-50 hover:border-cherry-200 transition-colors"
                                    >
                                        {action}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <ChatInput
                        inputValue={inputValue}
                        setInputValue={setInputValue}
                        handleSendMessage={handleSendMessage}
                        handleImageSelect={handleImageSelect}
                        selectedImage={selectedImage}
                        isUploading={isUploading}
                        language={language}
                    />
                </div>
            </div>

            {/* Toggle Button - Always visible in bottom-right */}
            <button
                onClick={toggleChat}
                className={`
                    w-14 h-14 md:w-16 md:h-16 rounded-full shadow-lg flex items-center justify-center text-white text-2xl md:text-3xl
                    transition-all duration-300 hover:scale-110 active:scale-95
                    ${isOpen ? 'bg-gray-400 rotate-90' : 'bg-gradient-to-r from-cherry-600 to-gold-500 animate-bounce-slow'}
                `}
                aria-label="Toggle Chatbot"
            >
                <FiMessageSquare />
                {!isOpen && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                )}
            </button>
        </div>
    );
};

export default Chatbot;
